import { useEffect } from 'react';
import { Box, Stack, Text, HStack } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import Body1 from '@components/typography/Body1';
import LeftArrow from '@components/icons/LeftArrow';
import Header from '@components/typography/Header';
import DRXRedirect from '@atoms/DRXRedirect';
import { PlanType } from '@enums/PlanType';
import { ContentState } from './utils';

const DrxGuide = ({
  contentState,
  isDisabled,
  loading,
  planType,
  planName,
  recYear,
  relayState,
  samlBody,
  setShowDrxGuide,
  topSavings,
  userId,
  bypassHeading = false,
}) => {
  const router = useRouter();
  const { pathname, query } = router;
  // Record DRC Guide View + content state
  useEffect(() => {
    router.push({
      pathname,
      query: {
        ...query,
        view: 'drxGuide',
        state: contentState,
      },
    });
  }, []);
  const videoSrc = 'https://player.vimeo.com/video/1126316437?title=0&byline=0&portrait=0';

  const firstLineText = "We're about to send you to our plan shopping website. ";

  // Default case
  let previewText = (
    <>
      {firstLineText}
      <Text as="span">It'll include your current plan - </Text>
      <Text as="span" fontWeight="bold">
        {planName}
      </Text>
      {' - as well as one or two plans with similar coverage.'}
    </>
  );

  if (contentState === ContentState.PrecallSurvey || ContentState.NoCurrentPlan) {
    previewText = null;
  }

  // If MAPD and Recommended || Pareto
  if ([ContentState.SwitchRecommended, ContentState.Pareto].includes(contentState) && planType === PlanType.MAPD) {
    previewText = (
      <>
        {firstLineText}
        <Text as="span">It'll include your current plan - </Text>
        <Text as="span" fontWeight="bold">
          {planName}
        </Text>
        {` - as well as one or two ${
          topSavings === 0
            ? 'plans with better coverage for you. '
            : 'lower cost plans that cover your doctors and drugs. '
        }`}
        If you want, you'll be able to enroll in one of these plans online.
      </>
    );
  }

  // If PDP and Recommended || Pareto
  if ([ContentState.SwitchRecommended, ContentState.Pareto].includes(contentState) && PlanType.PDP) {
    previewText = (
      <>
        {firstLineText}
        <Text as="span">It'll include your current plan - </Text>
        <Text as="span" fontWeight="bold">
          {planName}
        </Text>
        {` - as well as one or two ${
          topSavings === 0 ? 'plans with better coverage for you.' : 'lower cost plans that cover your drugs. '
        }`}
        If you want, you'll be able to enroll in one of these plans online.
      </>
    );
  }

  // If Optimal
  if (contentState === ContentState.Optimal) {
    previewText = (
      <>
        {firstLineText}
        <Text as="span">It'll include your current plan - </Text>
        <Text as="span" fontWeight="bold">
          {planName}
        </Text>
        {' - as well as one or two plans that cost the same or more than your current plan. '}
        If you want, you'll be able to enroll in one of these plans online.
      </>
    );
  }

  if (contentState === ContentState.drugsNotCovered) {
    previewText = (
      <>
        {firstLineText}
        <Text as="span">It'll include your current plan - </Text>
        <Text as="span" fontWeight="bold">
          {planName}
        </Text>
        {' - as well as one or two plans that have similar coverage. '}
        If you want, you'll be able to enroll in one of these plans online.
      </>
    );
  }

  return (
    <Stack alignItems="center" w="100%" maxW="1000px" spacing={6} py={4} maxH="1000px">
      <Stack spacing={4}>
        <HStack w="100%" gap={[1, 1, 2]} justifyContent={['center', 'center', 'left']}>
          {!bypassHeading && (
            <>
              <LeftArrow onClick={() => setShowDrxGuide(false)} mr={5} />
              <Header as="h3">How to choose a plan</Header>
            </>
          )}
        </HStack>

        {previewText && <Body1>{previewText}</Body1>}
        {previewText && <Body1>Watch the video below for instructions on how to choose and enroll in a plan.</Body1>}
      </Stack>
      <Box h={['220px', '300px', '500px']} w="100%" maxW="700px" mx="auto">
        <iframe title="Online Plan Reshop Guide" width="100%" height="100%" src={videoSrc} allowFullScreen />
      </Box>
      <DRXRedirect
        userId={userId}
        isDisabled={isDisabled}
        CTA={contentState === ContentState.PrecallSurvey ? 'View Plans' : "Ok, I'm Ready!"}
        samlBody={samlBody}
        loading={loading}
        relayState={relayState}
        recYear={recYear}
      />
    </Stack>
  );
};

export default DrxGuide;
